/**
 * Debug Mode Badge Component
 *
 * Shows a small "DEBUG MODE" badge in the bottom-right corner
 * when debug mode is active (?debug=true URL parameter).
 *
 * Features:
 * - Only visible when debug mode is enabled
 * - Subtle styling (small, semi-transparent)
 * - Click to disable debug mode
 * - Shows session persistence indicator
 */

import React, { useEffect, useState } from 'react';
import {
    isDebugMode,
    disableDebugMode,
    isDebugModeFromTestAccount,
} from '../utils/logger';

interface DebugModeBadgeProps {
    /** Optional additional class names */
    className?: string;
}

export const DebugModeBadge: React.FC<DebugModeBadgeProps> = ({
    className = '',
}) => {
    const [showBadge, setShowBadge] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFromTestAccount, setIsFromTestAccount] = useState(false);

    useEffect(() => {
        // Check debug mode on mount and when URL changes
        const checkDebugMode = () => {
            setShowBadge(isDebugMode());
            setIsFromTestAccount(isDebugModeFromTestAccount());
        };

        checkDebugMode();

        // Listen for URL changes (for SPA navigation)
        window.addEventListener('popstate', checkDebugMode);

        // Also check periodically in case debug mode is enabled after login
        const interval = setInterval(checkDebugMode, 1000);

        return () => {
            window.removeEventListener('popstate', checkDebugMode);
            clearInterval(interval);
        };
    }, []);

    const handleDisableDebug = () => {
        disableDebugMode();
        setShowBadge(false);

        // Remove ?debug=true from URL without page reload
        const url = new URL(window.location.href);
        url.searchParams.delete('debug');
        window.history.replaceState({}, '', url.toString());
    };

    if (!showBadge) {
        return null;
    }

    return (
        <div
            className={`fixed bottom-4 right-4 z-[9999] ${className}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <button
                onClick={handleDisableDebug}
                className={`
                    px-3 py-1.5
                    text-xs font-mono font-semibold
                    bg-yellow-500/90 text-black
                    rounded-md
                    shadow-lg
                    transition-all duration-200
                    ${isHovered ? 'bg-yellow-400 scale-105' : 'opacity-70'}
                    hover:opacity-100
                    focus:outline-none focus:ring-2 focus:ring-yellow-300
                `}
                title={
                    isFromTestAccount
                        ? 'Debug mode enabled (test account)'
                        : 'Click to disable debug mode'
                }
            >
                <span className="flex items-center gap-1.5">
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                        />
                    </svg>
                    {isFromTestAccount ? 'TEST ACCOUNT' : 'DEBUG MODE'}
                    {isHovered && (
                        <svg
                            className="w-3 h-3 ml-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    )}
                </span>
            </button>
        </div>
    );
};

export default DebugModeBadge;
